import React, { useState, useEffect } from 'react';
import { Button, Typography, InputBase } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { sendMessage as doSendMessage } from 'apis/trackstar/trackstar';
import { fetchAssetsDevicesShares } from 'apis/trackstar/serenity';
import useTranslation from 'hooks/useTranslation';
import moment from 'moment';
import uuid from 'utils/uuid';
import mixpanel from 'mixpanel-browser';
import useStyles from './messageInputBox-styles';

interface MessageInputBoxProps {
  isAssetConvo: boolean;
  disabled: boolean;
  displaySnackbar: (params: Snack) => void;
  deviceId: number;
  username: string;
  organisationName: string;
  organisationId: string;
}

const MessageInputBox = ({
  isAssetConvo,
  disabled = false,
  displaySnackbar,
  deviceId,
  username,
  organisationName,
  organisationId,
}: MessageInputBoxProps): JSX.Element => {
  const classes = useStyles();
  const t = useTranslation('pages.messaging');
  const MAX_MESSAGE_LENGTH = isAssetConvo ? 200 : 10000;
  const queryClient = useQueryClient();
  // track number of characters in text input box
  const [charCount, setCharCount] = useState(MAX_MESSAGE_LENGTH);
  // new message text
  const [newMsg, setNewMsg] = useState('');
  // flag that tracks if new message is valid
  const [invalidText, setInvalidText] = useState(true);

  const getTransportCode = useQuery(['assetsWithShares', organisationId, null], () => fetchAssetsDevicesShares(organisationId), {
    select: (assets: Asset[]): string => {
      const { device } = assets.find(a => a.device?.id === deviceId) ?? { device: undefined };
      if (!device) return '';
      const messagingCapabilities = device.capabilities && device.capabilities?.filter(c => c.code === 'CP_SND_TEXT');
      if (!messagingCapabilities?.length) return '';
      return messagingCapabilities.find(c => c.transportCode === 'IRDM_SBD')?.transportCode ?? messagingCapabilities[0].transportCode;
    },
  });
  if (getTransportCode.isError) {
    displaySnackbar({
      id: 'assetLoadError',
      text: getTransportCode?.error ? getTransportCode.isError.toString() : '',
      type: 'error',
    });
  }

  // TODO: get transport from deviceCapabilities for this deviceId
  const sendMessage = useMutation(() => doSendMessage(deviceId, `(${username}) ${newMsg}`, getTransportCode.isSuccess ? getTransportCode.data : ''), {
    onMutate: () => {
      // Optimistic update
      const prevConversation: any = queryClient.getQueryData(['messagesInf', deviceId]);
      const optimisticMessage = {
        content: `${organisationName}: '(${username}) ${newMsg}'`,
        conversationId: `conv-${deviceId}`,
        id: uuid(),
        isInbound: false,
        sender: { id: deviceId, ownerId: '', type: 'asset' },
        timestamp: moment().utc().format('MM/DD/YYYY HH:mm:ss'),
      };
      queryClient.setQueryData(
        ['messagesInf', deviceId],
        (old: any) => {
          const updatedFirstPage = [...old.pages[0], optimisticMessage];
          const restPages = old.pages.slice(1);
          return { pages: [updatedFirstPage, ...restPages], pageParams: old.pageParams };
        }
      );
      return { prevConversation };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['messagesInf', deviceId], context?.prevConversation);
      displaySnackbar({ id: 'sendMessageFailed', text: t('failed'), type: 'error' });
      mixpanel.track('Send Message', { success: false });
    },
    onSuccess: () => {
      displaySnackbar({ id: 'sendMessageSuccess', text: t('success'), type: 'success' });
      setNewMsg('');
      setCharCount(MAX_MESSAGE_LENGTH);
      mixpanel.track('Send Message', { success: true });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['messagesInf', deviceId]);
    },
    mutationKey: 'sendMessage'
  });

  useEffect(() => {
    if (newMsg?.trim() !== '') {
      setInvalidText(false);
    } else {
      setInvalidText(true);
    }
    setCharCount(MAX_MESSAGE_LENGTH - newMsg.length);
  }, [MAX_MESSAGE_LENGTH, newMsg]);

  return (
    <div className={classes.newMessage}>
      <InputBase
        autoFocus
        className={classes.newMessageText}
        placeholder={t('inputPlaceholder')}
        multiline
        minRows={5}
        maxRows={5}
        value={newMsg}
        disabled={disabled}
        onChange={(e): void => {
          const msgLen = e.target.value.length;
          if (msgLen <= MAX_MESSAGE_LENGTH) {
            setNewMsg(e.target.value);
          }
        }}
        onKeyDown={(e): void => {
          if (e.key === 'Enter') {
            e.preventDefault();
            // TODO: uncomment this to enable press return to send
            // newMsg?.trim() !== '' && sendMessage.mutate();
          }
        }}
      />
      <div className={classes.charCountSendDiv}>
        {isAssetConvo && <Typography className={classes.charCount} variant="body1">{charCount}</Typography>}
        <Button
          className={classes.sendButton}
          variant="contained"
          color="primary"
          onClick={() => newMsg?.trim() !== '' && sendMessage.mutate()}
          disabled={invalidText}
        >
          {t('sendButton')}
        </Button>
      </div>
    </div>
  );
};

export default MessageInputBox;
